// HU-29 (MG-ILE-57) · tarea 29-6. El buscador de producto del registro, DEL PAIS DEL CASO.
//
// Antes el maestro se cargaba una vez, entero y sin pais, en `CatalogosContexto`. Desde HU-29 el
// maestro es por pais (`producto_pais`): el mismo SKU lleva otro registro sanitario en Chile que en
// Peru (C12) y un pais sin filas ofrece las semillas rotuladas «dato de prueba» (C9). Por eso la
// lista depende del pais del borrador y se vuelve a pedir al cambiarlo.
//
// Tres salidas que la pantalla distingue (ui/MG-ILE-57.md, «Selector de producto»):
//   · productos       → el `Select` (con «dato de prueba» en las semillas);
//   · `textoVacio`     → el pais no ofrece nada: el texto del servidor (C8), no uno propio;
//   · `fallo`          → el GET fallo: el aviso de siempre, «El maestro de productos no esta
//                        disponible». Es otro estado que el vacio y no se confunden.
import { useEffect, useState } from 'react';
import { listarMaestroDeSku } from '../api/catalogos';
import type { ProductoDelMaestro } from '../api/catalogos';

/** Un producto con los nombres que el formulario ya leia (`nombre`, `forma`, `registro`). */
export type ProductoDelFormulario = {
  sku: string;
  nombre: string;
  forma: string;
  concentracion: string;
  registro: string;
  esDatoDePrueba: boolean;
  /**
   * CC-02 (CC2-04-C1): los registros vigentes por autoridad, la predeterminada primero. Con más de
   * uno, la opción del buscador los muestra todos con su autoridad («SENASA F.08 · SANIPES S-1»).
   */
  registros: { autoridad: string; numero: string }[];
};

/** Lo que la opción dice del registro: el de la ficha o, con varias autoridades, todos con la suya. */
export const registrosDeLaOpcion = (p: ProductoDelFormulario): string =>
  (p.registros.length > 1 ? p.registros.map((r) => `${r.autoridad} ${r.numero}`).join(' · ') : p.registro);

export type MaestroDelFormulario = {
  productos: ProductoDelFormulario[];
  textoVacio: string | null;
  fallo: boolean;
  cargando: boolean;
};

const aProductoDelFormulario = (p: ProductoDelMaestro): ProductoDelFormulario => ({
  sku: p.sku,
  nombre: p.nombre_comercial,
  forma: p.forma_farmaceutica ?? '',
  concentracion: p.concentracion ?? '',
  registro: p.registro_sanitario ?? '',
  esDatoDePrueba: p.es_dato_de_prueba === true,
  registros: (p.registros ?? []).map((r) => ({ autoridad: r.autoridad_nombre, numero: r.registro_sanitario })),
});

const PAIS_ISO2 = /^[A-Z]{2}$/;
const SIN_MAESTRO: MaestroDelFormulario = { productos: [], textoVacio: null, fallo: false, cargando: false };

export function useMaestroDelPais(pais: string | null | undefined): MaestroDelFormulario {
  const [maestro, setMaestro] = useState<MaestroDelFormulario>({ ...SIN_MAESTRO, cargando: true });

  useEffect(() => {
    if (!pais || !PAIS_ISO2.test(pais)) {
      setMaestro(SIN_MAESTRO);
      return undefined;
    }
    let vigente = true;
    setMaestro((m) => ({ ...m, cargando: true }));
    listarMaestroDeSku(pais)
      .then((respuesta) => {
        if (!vigente) return;
        setMaestro({
          productos: (respuesta?.productos ?? []).map(aProductoDelFormulario),
          textoVacio: respuesta?.texto_vacio ?? null,
          fallo: false,
          cargando: false,
        });
      })
      .catch(() => {
        // HU-04 se queda sin su desplegable; no se inventa un maestro. La seccion 5 lo dice.
        if (vigente) setMaestro({ ...SIN_MAESTRO, fallo: true });
      });
    return () => { vigente = false; };
  }, [pais]);

  return maestro;
}
